import type { Product } from '@repo/shopify';
import { localizePathForCurrentMarket } from '@/lib/markets';
import {
	PUBLIC_PRODUCT_HANDLE_LIMIT,
	PUBLIC_PRODUCT_LIST_LIMIT,
} from '@/lib/public-storefront-input';

export type ProductBlockSource = 'manual' | 'collection' | 'best-sellers' | 'new-arrivals';

export interface ProductBlockRequest {
	source: ProductBlockSource;
	maxProducts: number;
	collectionHandle?: string;
	productHandles?: string[];
}

function toProductBlockSource(source: string | undefined): ProductBlockSource {
	if (source === 'collection' || source === 'best-sellers' || source === 'new-arrivals') {
		return source;
	}

	return 'manual';
}

async function fetchProductsJson(url: string, signal?: AbortSignal) {
	const response = await fetch(url, { signal });
	if (!response.ok) return [];

	const result = (await response.json()) as { products?: unknown };
	return Array.isArray(result.products) ? (result.products as Product[]) : [];
}

export function createProductBlockRequest({
	source,
	maxProducts,
	collectionHandle,
	productHandles,
}: {
	source?: string;
	maxProducts?: number;
	collectionHandle?: string;
	productHandles?: string[];
}): ProductBlockRequest {
	const requestedMax =
		typeof maxProducts === 'number' && Number.isFinite(maxProducts) && maxProducts > 0
			? Math.floor(maxProducts)
			: 12;
	return {
		source: toProductBlockSource(source),
		maxProducts: Math.min(requestedMax, PUBLIC_PRODUCT_LIST_LIMIT),
		collectionHandle,
		productHandles,
	};
}

export async function fetchProductBlockProducts(
	request: ProductBlockRequest,
	signal?: AbortSignal,
) {
	if (request.source === 'collection') {
		if (!request.collectionHandle) return [];

		const params = new URLSearchParams({
			first: String(request.maxProducts),
			sort: 'collection-default',
		});
		return fetchProductsJson(
			localizePathForCurrentMarket(
				`/api/collections/${encodeURIComponent(request.collectionHandle)}/products?${params.toString()}`,
			),
			signal,
		);
	}

	if (request.source === 'best-sellers' || request.source === 'new-arrivals') {
		const params = new URLSearchParams({
			first: String(request.maxProducts),
			sort: request.source === 'new-arrivals' ? 'new-arrivals' : 'best-selling',
		});
		return fetchProductsJson(
			localizePathForCurrentMarket(`/api/products/list?${params.toString()}`),
			signal,
		);
	}

	const handles =
		request.productHandles?.filter(Boolean).slice(0, PUBLIC_PRODUCT_HANDLE_LIMIT) ?? [];
	if (handles.length === 0) return [];

	return fetchProductsJson(
		localizePathForCurrentMarket(`/api/products?handles=${encodeURIComponent(handles.join(','))}`),
		signal,
	);
}
